import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LangProvider } from "@/context/LangContext";
import { CartProvider } from "@/context/CartContext";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import CitiesPage from "@/pages/CitiesPage";
import CategoryPage from "@/pages/CategoryPage";
import VendorListing from "@/pages/VendorListing";
import VendorProfile from "@/pages/VendorProfile";
import ConsumerProfile from "@/pages/ConsumerProfile";
import BecomeVendor from "@/pages/BecomeVendor";
import VendorDashboard from "@/pages/VendorDashboard";
import ChatPage from "@/pages/ChatPage";
import CartPage from "@/pages/CartPage";
import NotificationCenter from "@/pages/NotificationCenter";
import AdminDashboard from "@/pages/AdminDashboard";
import Gharonda from "@/pages/apps/Gharonda";
import Khavayye from "@/pages/apps/Khavayye";
import SoulMates from "@/pages/apps/SoulMates";
import SabKuchFresh from "@/pages/apps/SabKuchFresh";
import Niramaya from "@/pages/apps/Niramaya";
import Rozgari from "@/pages/apps/Rozgari";
import InquiryFeed from "@/pages/inquiries/InquiryFeed";
import InquiryDetail from "@/pages/inquiries/InquiryDetail";
import SearchResults from "@/pages/SearchResults";
import ProductDetail from "@/pages/ProductDetail";
import { Pending, Terms, Privacy, Disclaimer, Contact } from "@/pages/StaticPages";

function AppRouter() {
  const location = useLocation();
  // CRITICAL: handle Emergent session_id BEFORE anything else, synchronously.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/pending" element={<ProtectedRoute allowPending><Pending /></ProtectedRoute>} />

        <Route path="/cities" element={<ProtectedRoute><CitiesPage /></ProtectedRoute>} />
        <Route path="/categories" element={<ProtectedRoute><CategoryPage /></ProtectedRoute>} />
        <Route path="/vendors" element={<ProtectedRoute><VendorListing /></ProtectedRoute>} />
        <Route path="/vendors/:vendorId" element={<VendorProfile />} />

        <Route path="/profile" element={<ProtectedRoute allowPending><ConsumerProfile /></ProtectedRoute>} />
        <Route path="/become-vendor" element={<ProtectedRoute><BecomeVendor /></ProtectedRoute>} />
        <Route path="/dashboard/vendor" element={<ProtectedRoute><VendorDashboard /></ProtectedRoute>} />

        <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
        <Route path="/chat/:chatId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute allowPending><NotificationCenter /></ProtectedRoute>} />

        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />

        {/* Apps mega-menu */}
        <Route path="/apps/gharonda" element={<ProtectedRoute><Gharonda /></ProtectedRoute>} />
        <Route path="/apps/khavayye" element={<ProtectedRoute><Khavayye /></ProtectedRoute>} />
        <Route path="/apps/soulmates" element={<ProtectedRoute><SoulMates /></ProtectedRoute>} />
        <Route path="/apps/sabkuchfresh" element={<ProtectedRoute><SabKuchFresh /></ProtectedRoute>} />
        <Route path="/apps/niramaya" element={<ProtectedRoute><Niramaya /></ProtectedRoute>} />
        <Route path="/apps/rozgari" element={<ProtectedRoute><Rozgari /></ProtectedRoute>} />

        {/* Inquiry threads */}
        <Route path="/inquiries" element={<ProtectedRoute><InquiryFeed /></ProtectedRoute>} />
        <Route path="/inquiries/:inquiryId" element={<ProtectedRoute><InquiryDetail /></ProtectedRoute>} />

        {/* Unified search / items */}
        <Route path="/search" element={<ProtectedRoute><SearchResults /></ProtectedRoute>} />
        <Route path="/items" element={<ProtectedRoute><SearchResults /></ProtectedRoute>} />
        <Route path="/items/:productKey" element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} />

        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/contact" element={<Contact />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <LangProvider>
        <BrowserRouter>
          <AuthProvider>
            <CartProvider>
              <AppRouter />
              <Toaster position="top-right" richColors />
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </LangProvider>
    </ThemeProvider>
  );
}
